import SectionCard from "components/cms/SectionCard";
import {
  AreaInput,
  CardRow,
  FieldRow,
  FileInput,
  GroupBox,
  TextInput,
} from "components/cms/fields";

const HERO_KEYS = [
  "heroTag",
  "heroTitle1",
  "heroTitle2",
  "heroDescription",
  "heroPrimaryCtaText",
  "heroPrimaryCtaLink",
  "heroSecondaryCtaText",
  "heroSecondaryCtaLink",
  "heroTrustText",
  "heroLogoCard",
  "heroCard1",
  "heroCard2",
  "heroSectionHide",
];
const BUILD_KEYS = [
  "buildTag",
  "buildTitleLead",
  "buildTitleAccent",
  "buildTitleTail",
  "buildBody",
  "pillar1",
  "pillar2",
  "pillar3",
  "buildHide",
];
const STATS_KEYS = ["stat1", "stat2", "stat3", "stat4", "statsHide"];
const FAQ_KEYS = ["faqTag", "faqTitleLead", "faqTitleAccent", "faqItems", "faqHide"];

const featureCard = (label, path, form, setField, frMap) => (
  <GroupBox title={label}>
    <TextInput
      label="Title"
      path={`${path}.title`}
      form={form}
      setField={setField}
      frMap={frMap}
    />
    <AreaInput
      label="Description"
      path={`${path}.description`}
      form={form}
      setField={setField}
      frMap={frMap}
    />
  </GroupBox>
);

export default function PricingStack({
  heading,
  description,
  idPrefix,
  form,
  setField,
  frMap,
  exists,
  savePricing,
  savingSection,
  isSectionDirty,
}) {
  return (
    <>
      <p className="mb-2 mt-6 text-sm font-semibold text-navy-700 dark:text-white">
        {heading}
      </p>
      {description ? (
        <p className="mb-3 text-xs text-gray-500">{description}</p>
      ) : null}

      <SectionCard
        title="Hero"
        hide={form.heroSectionHide}
        hideId={`${idPrefix}-heroHide`}
        onHideChange={(value) => setField("heroSectionHide", value)}
        onSave={() => savePricing("hero", HERO_KEYS)}
        saving={savingSection === "hero" || savingSection === "all"}
        canSave={!exists || isSectionDirty(HERO_KEYS)}
      >
        <FieldRow>
          <TextInput
            label="Tag"
            path="heroTag"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Title line 1"
            path="heroTitle1"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Title line 2"
            path="heroTitle2"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <AreaInput
          label="Description"
          path="heroDescription"
          form={form}
          setField={setField}
          frMap={frMap}
        />
        <FieldRow>
          <TextInput
            label="Primary CTA text"
            path="heroPrimaryCtaText"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Primary CTA link"
            path="heroPrimaryCtaLink"
            form={form}
            setField={setField}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Secondary CTA text"
            path="heroSecondaryCtaText"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Secondary CTA link"
            path="heroSecondaryCtaLink"
            form={form}
            setField={setField}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Trust text"
            path="heroTrustText"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <FileInput
          label="Logo card image"
          path="heroLogoCard.image"
          form={form}
          setField={setField}
        />
        <FieldRow>
          <TextInput
            label="Logo card text"
            path="heroLogoCard.text"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <CardRow>
          {featureCard("Hero card 1", "heroCard1", form, setField, frMap)}
          {featureCard("Hero card 2", "heroCard2", form, setField, frMap)}
        </CardRow>
      </SectionCard>

      <SectionCard
        title="How we build"
        hide={form.buildHide}
        hideId={`${idPrefix}-buildHide`}
        onHideChange={(value) => setField("buildHide", value)}
        onSave={() => savePricing("build", BUILD_KEYS)}
        saving={savingSection === "build" || savingSection === "all"}
        canSave={!exists || isSectionDirty(BUILD_KEYS)}
      >
        <FieldRow>
          <TextInput
            label="Tag"
            path="buildTag"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Title lead"
            path="buildTitleLead"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Title accent"
            path="buildTitleAccent"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Title tail"
            path="buildTitleTail"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <AreaInput
          label="Body"
          path="buildBody"
          form={form}
          setField={setField}
          frMap={frMap}
        />
        <CardRow cols="md:grid-cols-3">
          {["pillar1", "pillar2", "pillar3"].map((key, index) => (
            <GroupBox key={key} title={`Pillar ${index + 1}`}>
              <TextInput
                label="Number"
                path={`${key}.n`}
                form={form}
                setField={setField}
              />
              <TextInput
                label="Title"
                path={`${key}.title`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
              <AreaInput
                label="Description"
                path={`${key}.description`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
            </GroupBox>
          ))}
        </CardRow>
      </SectionCard>

      <SectionCard
        title="Stats"
        hide={form.statsHide}
        hideId={`${idPrefix}-statsHide`}
        onHideChange={(value) => setField("statsHide", value)}
        onSave={() => savePricing("stats", STATS_KEYS)}
        saving={savingSection === "stats" || savingSection === "all"}
        canSave={!exists || isSectionDirty(STATS_KEYS)}
      >
        <CardRow>
          {["stat1", "stat2", "stat3", "stat4"].map((key, index) => (
            <GroupBox key={key} title={`Stat ${index + 1}`}>
              <TextInput
                label="Number"
                path={`${key}.number`}
                form={form}
                setField={setField}
              />
              <TextInput
                label="Label"
                path={`${key}.label`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
            </GroupBox>
          ))}
        </CardRow>
      </SectionCard>

      <SectionCard
        title="FAQ"
        hide={form.faqHide}
        hideId={`${idPrefix}-faqHide`}
        onHideChange={(value) => setField("faqHide", value)}
        onSave={() => savePricing("faq", FAQ_KEYS)}
        saving={savingSection === "faq" || savingSection === "all"}
        canSave={!exists || isSectionDirty(FAQ_KEYS)}
      >
        <FieldRow>
          <TextInput
            label="Tag"
            path="faqTag"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <FieldRow>
          <TextInput
            label="Title lead"
            path="faqTitleLead"
            form={form}
            setField={setField}
            frMap={frMap}
          />
          <TextInput
            label="Title accent"
            path="faqTitleAccent"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>
        <CardRow>
          {(form.faqItems || []).map((_, index) => (
            <GroupBox key={`faq-${index}`} title={`Question ${index + 1}`}>
              <TextInput
                label="Question"
                path={`faqItems.${index}.question`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
              <AreaInput
                label="Answer"
                path={`faqItems.${index}.answer`}
                form={form}
                setField={setField}
                frMap={frMap}
                rows={4}
              />
            </GroupBox>
          ))}
        </CardRow>
      </SectionCard>
    </>
  );
}
